/* I documenti: dal maturato all'avviso, dall'avviso alla fattura.
 *
 * E' la meta' del programma che lavora per chi fattura, e segue il ciclo che
 * il dominio impone, non uno inventato qui:
 *
 * 1. **da fatturare**: il maturato di ogni pratica, raggruppato per cliente.
 *    Si scelgono le pratiche di un cliente solo, perche' un documento ha un
 *    solo regime fiscale, e l'avviso nasce **in bozza**;
 * 2. **la bozza si controlla** e poi si segna come mandata al cliente. Finche'
 *    non e' incassata si puo' annullare, e le sue ore tornano da fatturare;
 * 3. **l'incasso prepara la fattura**, con la data dell'incasso e non quella
 *    dell'avviso. E' l'errore che tutto il progetto esiste per impedire, e per
 *    questo la data dell'incasso si chiede sempre;
 * 4. **la fattura si passa su Fatture Smart** (dal 25 settembre 2026): la
 *    scheda dice cosa copiare, e il numero che Fatture Smart le da' torna qui.
 *    Va fatto entro 12 giorni dall'incasso, e la coda lo conta;
 * 5. una fattura non si annulla: **si storna** con una nota di credito, fatta
 *    anche lei su Fatture Smart.
 *
 * ## Cosa questa pagina non fa, e lo dice
 *
 * **Non emette la fattura elettronica e non ne fa il PDF.** Li fa Fatture
 * Smart: una seconda copia fatta qui sarebbe una seconda versione dello stesso
 * documento fiscale. Il PDF qui si fa solo per l'avviso di parcella.
 *
 * **Non calcola niente.** Tutti gli importi arrivano dal server e restano
 * stringhe fino allo schermo. L'unica aritmetica qui dentro e' la somma di cio'
 * che si sta per mettere in un avviso, ed e' esatta al centesimo (`somma`).
 */

import { useMemo, useState, type FormEvent } from "react";
import { Link, Navigate, useLocation, useOutletContext, useSearchParams } from "react-router-dom";
import { ErroreApi, anagrafica, documenti, incassi, pdfAvvisoAttivo, type NuovoAvviso } from "../api";
import { useCarica } from "../carica";
import { useSessione } from "../sessione";
import { APRI_PRESTAZIONI_DA_RIVEDERE, CodiciDaRivedere } from "../componenti/CodiciDaRivedere";
import { FatturaAcconto } from "../componenti/FatturaAcconto";
import { FatturaNormale } from "../componenti/FatturaNormale";
import { Interruttore } from "../componenti/Interruttore";
import { SchedaCopia } from "../componenti/SchedaCopia";
import { TestoFattura } from "../componenti/TestoFattura";
import { BarraSchede, VISTE_FATTURARE } from "../componenti/Schede";
import { TestaPagina } from "../componenti/TestaPagina";
import type { ContestoGuscio } from "../componenti/Guscio";
import { urgenzaFatture } from "../urgenzaFatture";
import {
  MODALITA,
  confronta,
  conta,
  dataBreve,
  dataCompleta,
  differenza,
  giorniDa,
  eZero,
  euro,
  importoDaScritto,
  nomeDocumento,
  nomeStatoDocumento,
  oggi,
  oreEMinuti,
  scadenzaFattura,
  somma,
  trova,
  type Cliente,
  type DaPassare,
  type Documento,
  type Incasso,
  type Maturato,
  type NomeModalita,
  type VersamentoDaFatturare,
} from "../dominio";

function messaggio(e: unknown, ripiego: string): string {
  return e instanceof ErroreApi ? e.message : ripiego;
}

/* I passi del lavoro di chi fattura, ognuno una scheda col suo conteggio:
 * dal 25 settembre 2026 al posto di una pagina sola con tutto uno sotto
 * l'altro. Il numero dice se in quel passo c'e' lavoro, come i conteggi di
 * Linear accanto alle viste. */
const VISTE = VISTE_FATTURARE;

export function Documenti() {
  const { ricaricaCoda } = useOutletContext<ContestoGuscio>();
  const { pathname } = useLocation();
  const [parametri, setParametri] = useSearchParams();
  const maturato = useCarica<Maturato[]>(() => documenti.daFatturare(), []);
  const elenco = useCarica<Documento[]>(() => documenti.elenco(), []);
  const clienti = useCarica<Cliente[]>(() => anagrafica.clienti(), []);
  const coda = useCarica<DaPassare[]>(() => documenti.daPassare(), []);
  // Gli acconti e i saldi che il programma non ha trasformato in fattura: si passano a mano.
  const aMano = useCarica<VersamentoDaFatturare[]>(() => incassi.daFatturare(), []);
  // Il documento aperto vale nella scheda in cui e' stato aperto: cambiare
  // scheda torna all'elenco, invece di lasciare un dettaglio che non c'entra.
  const aperto = parametri.get("documento");
  // La fattura di un acconto o saldo da fare a mano: vale nella scheda "Fatture Smart".
  const acconto = parametri.get("acconto");
  const praticaId = parametri.get("pratica");
  // Dalla scheda di un cliente si arriva qui con il suo nome gia' nella ricerca.
  const cercaIniziale = parametri.get("cerca") ?? "";
  const togliParametro = (nome: string) => setParametri((attuali) => {
    const prossimi = new URLSearchParams(attuali);
    prossimi.delete(nome);
    return prossimi;
  });

  const vista = pathname.split("/")[2] ?? "";

  async function ricaricaTutto() {
    await Promise.all([
      maturato.ricarica(),
      elenco.ricarica(),
      coda.ricarica(),
      aMano.ricarica(),
      ricaricaCoda(),
    ]);
  }

  const errore = maturato.errore ?? elenco.errore ?? clienti.errore ?? coda.errore ?? aMano.errore;
  const pronto =
    maturato.dati !== undefined &&
    elenco.dati !== undefined &&
    clienti.dati !== undefined &&
    coda.dati !== undefined &&
    aMano.dati !== undefined;

  // Arrivando su "Fatturare" senza scheda si va dove c'e' il lavoro piu'
  // urgente: le fatture da emettere, che hanno una scadenza, se ce ne sono.
  if (pronto && !VISTE.some((v) => v.percorso === vista)) {
    const prima = (coda.dati ?? []).length + (aMano.dati ?? []).length > 0 ? "fatture-smart" : "da-fatturare";
    return <Navigate to={`/fatturare/${prima}`} replace />;
  }

  const apri = (id: string) => setParametri((attuali) => {
    const prossimi = new URLSearchParams(attuali);
    prossimi.set("documento", id);
    return prossimi;
  });
  const apriAcconto = (gruppo: string) => setParametri((attuali) => {
    const prossimi = new URLSearchParams(attuali);
    prossimi.set("acconto", gruppo);
    prossimi.delete("documento");
    return prossimi;
  });
  const documentiTutti = elenco.dati ?? [];
  // Una coda sola, dalla piu' urgente: le fatture e gli acconti hanno la stessa scadenza di legge.
  const daFare = vociDaFare(coda.dati ?? [], aMano.dati ?? []);
  const apriVoce = (v: VoceDaFare) => (v.tipo === "acconto" ? apriAcconto(v.id) : apri(v.id));
  const posizioneDi = (chiave: string) => {
    const i = daFare.findIndex((v) => v.chiave === chiave);
    return i >= 0 ? { i: i + 1, n: daFare.length } : null;
  };
  // Dopo aver registrato una fattura si passa alla successiva, la piu' urgente rimasta.
  const passaOltre = (chiave: string) => {
    const altre = daFare.filter((v) => v.chiave !== chiave);
    const i = daFare.findIndex((v) => v.chiave === chiave);
    const prossima = altre[Math.max(i, 0)] ?? altre[0];
    if (prossima) apriVoce(prossima);
    else {
      togliParametro("documento");
      togliParametro("acconto");
    }
  };
  // I numeri gia' registrati, per rifiutare un doppione e suggerire il successivo.
  const numeriUsati = [
    ...documentiTutti.filter((d) => d.tipo === "FATTURA" && d.numero).map((d) => String(d.numero)),
    ...documentiTutti.flatMap((d) =>
      d.incassi.filter((v) => !v.annullato && v.fattura_riferimento).map((v) => v.fattura_riferimento),
    ),
  ];
  const conteggi: Record<string, number> = {
    "da-fatturare": (maturato.dati ?? []).filter((m) => !eZero(m.da_fatturare)).length,
    avvisi: documentiTutti.filter(
      (d) => d.tipo === "AVVISO" && (d.stato === "BOZZA" || d.stato === "EMESSO"),
    ).length,
    "fatture-smart": (coda.dati ?? []).length + (aMano.dati ?? []).length,
  };

  return (
    <>
      <TestaPagina
        titolo="Fatturare"
        sottotitolo={
          coda.dati !== undefined && aMano.dati !== undefined
            ? urgenzaFatture([...coda.dati, ...aMano.dati])
            : undefined
        }
      />

      <BarraSchede
        base="/fatturare"
        voci={VISTE.map((v) => ({ ...v, conteggio: conteggi[v.percorso] }))}
      />

      {errore && (
        <p className="errore" role="alert">
          {errore}
        </p>
      )}

      {!pronto ? (
        errore ? null : <p className="attesa">Un momento.</p>
      ) : aperto !== null ? (
        <Dettaglio
          key={aperto}
          identificativo={aperto}
          tutti={documentiTutti}
          apri={apri}
          chiudi={() => togliParametro("documento")}
          dopo={ricaricaTutto}
          posizioneDi={posizioneDi}
          numeriUsati={numeriUsati}
          registrato={async (id) => {
            await ricaricaTutto();
            passaOltre(`f:${id}`);
          }}
        />
      ) : acconto !== null && vista === "fatture-smart" ? (
        <FatturaAcconto
          key={acconto}
          gruppo={acconto}
          posizione={posizioneDi(`a:${acconto}`)}
          numeriUsati={numeriUsati}
          chiudi={() => togliParametro("acconto")}
          apriAvviso={apri}
          registrato={async () => {
            await ricaricaTutto();
            passaOltre(`a:${acconto}`);
          }}
        />
      ) : vista === "da-fatturare" ? (
        <>
          <CodiciDaRivedere dopo={maturato.ricarica} />
          <DaFatturare
            maturato={maturato.dati ?? []}
            clienti={clienti.dati ?? []}
            praticaId={praticaId}
            cercaIniziale={cercaIniziale}
            togliFiltro={() => togliParametro("pratica")}
            creato={async (avviso) => {
              await ricaricaTutto();
              apri(avviso.identificativo);
            }}
          />
        </>
      ) : vista === "fatture-smart" ? (
        <DaFare voci={daFare} apri={apriVoce} />
      ) : (
        <Registro
          documenti={documentiTutti}
          apri={apri}
          solo={vista === "avvisi" ? "avvisi" : "archivio"}
        />
      )}
    </>
  );
}

// ------------------------------------------------------------- da fatturare

interface Scelta {
  cliente_id: string;
  pratiche: string[];
}

type OrdineLista = "vecchi" | "importo" | "nome";

/** Da quanti giorni il lavoro piu' vecchio fa di una pratica una pratica "ferma". */
const GIORNI_FERMA = 60;

/** Quanti clienti mostra la lista alla volta: con cento clienti e piu' una pagina di venti schermate non si
 * legge. Cercando o filtrando si vedono tutti quelli che corrispondono. */
const CLIENTI_PER_VOLTA = 25;

function DaFatturare({
  maturato,
  clienti,
  creato,
  praticaId,
  cercaIniziale,
  togliFiltro,
}: {
  maturato: Maturato[];
  clienti: Cliente[];
  creato: (avviso: Documento) => Promise<void>;
  praticaId: string | null;
  cercaIniziale: string;
  togliFiltro: () => void;
}) {
  const [scelta, setScelta] = useState<Scelta | null>(null);
  const [cerca, setCerca] = useState(cercaIniziale);
  const [ordine, setOrdine] = useState<OrdineLista>("vecchi");
  const [soloFerme, setSoloFerme] = useState(false);
  const [quanti, setQuanti] = useState(CLIENTI_PER_VOLTA);

  const clientiPerId = useMemo(
    () => new Map(clienti.map((c) => [c.identificativo, c])),
    [clienti],
  );

  const visibili = maturato.filter((m) => !praticaId || m.pratica_id === praticaId);
  const fatturabile = visibili.filter((m) => !eZero(m.da_fatturare));

  /** I clienti con le loro pratiche, dopo ricerca e filtro, nell'ordine scelto. Di default prima chi aspetta
   * da piu' tempo: e' il lavoro che si perde piu' facilmente. */
  const gruppi = useMemo(() => {
    const per = new Map<string, Maturato[]>();
    for (const m of visibili) {
      const nome = clientiPerId.get(m.cliente_id)?.denominazione ?? "";
      if (!trova(cerca, nome, m.etichetta)) continue;
      if (soloFerme && !(m.dal !== null && giorniDa(m.dal) > GIORNI_FERMA)) continue;
      per.set(m.cliente_id, [...(per.get(m.cliente_id) ?? []), m]);
    }
    const righe = [...per.entries()].map(([id, sue]) => ({
      id,
      sue: [...sue].sort((a, b) => (a.dal ?? "9999") < (b.dal ?? "9999") ? -1 : 1),
      nome: clientiPerId.get(id)?.denominazione ?? "Cliente non trovato",
      totale: somma(sue.map((m) => m.da_fatturare)),
      dal: sue.map((m) => m.dal).filter((d): d is string => d !== null).sort()[0] ?? null,
    }));
    righe.sort((a, b) => {
      if (ordine === "importo") return Number(b.totale) - Number(a.totale) || a.nome.localeCompare(b.nome, "it");
      if (ordine === "nome") return a.nome.localeCompare(b.nome, "it");
      const da = a.dal ?? "9999";
      const db = b.dal ?? "9999";
      return da < db ? -1 : da > db ? 1 : a.nome.localeCompare(b.nome, "it");
    });
    return righe;
  }, [visibili, clientiPerId, cerca, soloFerme, ordine]);

  const pratiche = gruppi.reduce((n, g) => n + g.sue.length, 0);
  const filtrata = cerca.trim() !== "" || soloFerme;
  const mostrati = filtrata ? gruppi : gruppi.slice(0, quanti);

  /** Scegliere una pratica di un altro cliente ricomincia la scelta invece di
   * sommarla: un avviso ha un solo intestatario e un solo regime fiscale, e il
   * server rifiuterebbe comunque. Meglio non poterlo nemmeno comporre. */
  function commuta(m: Maturato) {
    setScelta((attuale) => {
      if (attuale === null || attuale.cliente_id !== m.cliente_id) {
        return { cliente_id: m.cliente_id, pratiche: [m.pratica_id] };
      }
      const dentro = attuale.pratiche.includes(m.pratica_id);
      const pratiche = dentro
        ? attuale.pratiche.filter((p) => p !== m.pratica_id)
        : [...attuale.pratiche, m.pratica_id];
      return pratiche.length === 0 ? null : { ...attuale, pratiche };
    });
  }

  /** Tutte le pratiche fatturabili di un cliente in un gesto; di nuovo, le toglie. */
  function scegliTutte(clienteId: string, sue: Maturato[]) {
    const possibili = sue.filter((m) => !eZero(m.da_fatturare)).map((m) => m.pratica_id);
    setScelta((attuale) => {
      const tutteDentro =
        attuale !== null && attuale.cliente_id === clienteId && possibili.every((p) => attuale.pratiche.includes(p));
      if (tutteDentro || possibili.length === 0) return null;
      return { cliente_id: clienteId, pratiche: possibili };
    });
  }

  return (
    <section className="elenco elenco-primo">
      {praticaId && <p className="aiuto">
        Pratica aperta dal cruscotto. <button type="button" className="tasto tasto-nudo"
          onClick={() => { setScelta(null); togliFiltro(); }}>Mostra tutte le pratiche</button>
      </p>}
      {visibili.length === 0 ? (
        <p className="aiuto">
          {praticaId ? "Questa pratica non ha più lavoro da fatturare."
            : "Niente da fatturare: nessuna pratica ha ore non ancora fatturate o un prezzo concordato da mettere in un avviso."}
        </p>
      ) : (
        <>
          <p className="aiuto">
            {conta(fatturabile.length, "pratica", "pratiche")} con qualcosa da
            fatturare, per {euro(somma(fatturabile.map((m) => m.da_fatturare)))} in
            tutto. Scegli le pratiche di un cliente, o «Scegli tutte»: l'avviso nasce
            in bozza e lo controlli prima di mandarlo.
          </p>

          <div className="barra-lista">
            <div className="campo campo-largo">
              <label htmlFor="cerca-da-fatturare">Cerca</label>
              <input
                id="cerca-da-fatturare"
                type="search"
                value={cerca}
                placeholder="un cliente o una pratica"
                onChange={(e) => setCerca(e.target.value)}
              />
            </div>
            <div className="campo">
              <label htmlFor="ordine-da-fatturare">Ordina</label>
              <select id="ordine-da-fatturare" value={ordine} onChange={(e) => setOrdine(e.target.value as OrdineLista)}>
                <option value="vecchi">Prima il lavoro più vecchio</option>
                <option value="importo">Prima l'importo più alto</option>
                <option value="nome">Per nome del cliente</option>
              </select>
            </div>
            <label className="barra-lista-spunta">
              <input type="checkbox" checked={soloFerme} onChange={(e) => setSoloFerme(e.target.checked)} />
              <span>Solo ferme da oltre {GIORNI_FERMA} giorni</span>
            </label>
          </div>
          {(cerca.trim() !== "" || soloFerme) && (
            <p className="narrativa">
              {pratiche === 0
                ? "Nessuna pratica corrisponde."
                : `${conta(pratiche, "pratica", "pratiche")} di ${visibili.length}.`}
            </p>
          )}

          {mostrati.map(({ id: clienteId, sue, nome, totale, dal }) => {
            const cliente = clientiPerId.get(clienteId);
            const scelteQui = scelta?.cliente_id === clienteId ? scelta.pratiche : [];
            const fatturabili = sue.filter((m) => !eZero(m.da_fatturare));
            const tutteScelte = fatturabili.length > 0 && fatturabili.every((m) => scelteQui.includes(m.pratica_id));
            return (
              <div key={clienteId} className="gruppo">
                <div className="gruppo-testa">
                  <div className="gruppo-titolo">
                    <h3>{nome}</h3>
                    {dal !== null && (
                      <span className="narrativa">il lavoro più vecchio è di {conta(giorniDa(dal), "giorno", "giorni")}</span>
                    )}
                  </div>
                  <div className="gruppo-azioni">
                    {fatturabili.length > 1 && (
                      <button type="button" className="tasto tasto-piccolo" onClick={() => scegliTutte(clienteId, sue)}>
                        {tutteScelte ? "Togli la scelta" : "Scegli tutte"}
                      </button>
                    )}
                    <span className="cifra">{euro(totale)}</span>
                  </div>
                </div>
                <table className="tabella">
                  <tbody>
                    {sue.map((m) => (
                      <RigaMaturato
                        key={m.pratica_id}
                        m={m}
                        nomeCliente={cliente?.denominazione ?? ""}
                        scelta={scelteQui.includes(m.pratica_id)}
                        commuta={() => commuta(m)}
                      />
                    ))}
                  </tbody>
                </table>
                {scelteQui.length > 0 && cliente && (
                  <Composizione
                    cliente={cliente}
                    scelte={sue.filter((m) => scelteQui.includes(m.pratica_id))}
                    lascia={() => setScelta(null)}
                    creato={async (avviso) => {
                      setScelta(null);
                      await creato(avviso);
                    }}
                  />
                )}
              </div>
            );
          })}
          {mostrati.length < gruppi.length && (
            <p>
              <button type="button" className="tasto" onClick={() => setQuanti((q) => q + CLIENTI_PER_VOLTA)}>
                Mostra altri clienti ({gruppi.length - mostrati.length} ancora nascosti)
              </button>
            </p>
          )}
        </>
      )}
    </section>
  );
}

/** L'etichetta della pratica senza il nome del cliente davanti: e' gia'
 * scritto nella testa del gruppo, e ripeterlo su ogni riga fa leggere il
 * nome dieci volte invece del lavoro. */
/** "Bianchi SRL - bilancio - 2026 - di esercizio" sotto il titolo Bianchi SRL
 * diventa "Bilancio 2026, di esercizio": il cliente e' gia' nel titolo del
 * gruppo, e i trattini sono la forma del server, non una frase. */
function senzaCliente(etichetta: string, nomeCliente: string): string {
  const prefisso = `${nomeCliente} - `;
  const resto =
    nomeCliente && etichetta.startsWith(prefisso) ? etichetta.slice(prefisso.length) : etichetta;
  const [tipo, periodo, ...altro] = resto.split(" - ");
  if (tipo === undefined || periodo === undefined) return resto;
  const testa = `${tipo.charAt(0).toUpperCase()}${tipo.slice(1)} ${periodo}`;
  return altro.length > 0 ? `${testa}, ${altro.join(" - ")}` : testa;
}

function RigaMaturato({
  m,
  nomeCliente,
  scelta,
  commuta,
}: {
  m: Maturato;
  nomeCliente: string;
  scelta: boolean;
  commuta: () => void;
}) {
  const niente = eZero(m.da_fatturare);
  const id = `pratica-${m.pratica_id}`;
  return (
    <tr
      className={`${scelta ? "riga-scelta " : ""}${niente ? "" : "riga-cliccabile"}`.trim() || undefined}
      onClick={(e) => {
        // Tutta la riga sceglie la pratica, non solo la casella: il bersaglio e' grande come la riga.
        if (niente || (e.target as HTMLElement).closest("label,input,a,button")) return;
        commuta();
      }}
    >
      <td className="colonna-spunta">
        <input
          id={id}
          type="checkbox"
          checked={scelta}
          disabled={niente}
          onChange={commuta}
        />
      </td>
      <td>
        <label htmlFor={id} className="pratica-nome">
          {senzaCliente(m.etichetta, nomeCliente)}
        </label>
        <span className="narrativa">
          {m.minuti > 0 ? `${oreEMinuti(m.minuti)} da fatturare` : "nessuna ora da fatturare"}
          {m.dal !== null && `, il più vecchio di ${conta(giorniDa(m.dal), "giorno", "giorni")}`}
          {m.prezzo_concordato !== null &&
            !m.prezzo_gia_fatturato &&
            `, prezzo concordato ${euro(m.prezzo_concordato)}`}
        </span>
        {/* Il backend segna "gia' fatturato" ogni pratica comparsa in un avviso,
            anche a ore: il messaggio vale solo per un prezzo concordato. */}
        {m.prezzo_concordato !== null && m.prezzo_gia_fatturato && (
          <span className="segno-fatturata">
            Il prezzo concordato è già in un avviso: queste ore servono a misurare
            quanto è costata la pratica, non si fatturano di nuovo.
          </span>
        )}
        {m.senza_tariffa.length > 0 && (
          <span className="segno-ritardo">
            {conta(m.senza_tariffa.length, "registrazione", "registrazioni")} senza
            tariffa: non entrano nell'avviso finché il listino non le copre.
          </span>
        )}
      </td>
      <td className="cifra colonna-importo">{euro(m.da_fatturare)}</td>
    </tr>
  );
}

// ------------------------------------------------------ comporre un avviso

interface VoceScritta {
  descrizione: string;
  importo: string;
}

function Composizione({
  cliente,
  scelte,
  lascia,
  creato,
}: {
  cliente: Cliente;
  scelte: Maturato[];
  lascia: () => void;
  creato: (avviso: Documento) => Promise<void>;
}) {
  const [descrizione, setDescrizione] = useState("");
  const [data, setData] = useState(oggi());
  const [spese, setSpese] = useState<VoceScritta[]>([]);
  const [forfettarie, setForfettarie] = useState<VoceScritta[]>([]);
  const [bollo, setBollo] = useState(true);
  // Precompilata dall'anagrafica: cambiarla e' un'eccezione, e il server la
  // manda solo quando diverge, cosi' resta scritta sul documento.
  const [ritenuta, setRitenuta] = useState(cliente.sostituto_imposta);
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  /** Le voci scritte a mano diventano importi del backend, o si fermano
   * dicendo quale riga non va. Una riga lasciata del tutto vuota si ignora:
   * e' il posto in piu' che uno apre e poi non usa. */
  function leggiVoci(voci: VoceScritta[], nome: string): VoceScritta[] | string {
    const fuori: VoceScritta[] = [];
    for (const [indice, v] of voci.entries()) {
      if (!v.descrizione.trim() && !v.importo.trim()) continue;
      const importo = importoDaScritto(v.importo);
      if (!v.descrizione.trim()) return `${nome}, riga ${indice + 1}: manca la descrizione.`;
      if (importo === null) return `${nome}, riga ${indice + 1}: l'importo non si legge.`;
      fuori.push({ descrizione: v.descrizione.trim(), importo });
    }
    return fuori;
  }

  async function invia(evento: FormEvent) {
    evento.preventDefault();
    setErrore(null);
    const lette = leggiVoci(spese, "Spese anticipate");
    if (typeof lette === "string") return setErrore(lette);
    const letteF = leggiVoci(forfettarie, "Spese forfettarie");
    if (typeof letteF === "string") return setErrore(letteF);

    const corpo: NuovoAvviso = {
      pratiche_id: scelte.map((m) => m.pratica_id),
      descrizione,
      data,
      spese: lette,
      forfettarie: letteF,
      riaddebita_bollo: bollo,
      ...(ritenuta !== cliente.sostituto_imposta ? { ritenuta } : {}),
    };
    setInCorso(true);
    try {
      await creato(await documenti.creaAvviso(corpo));
    } catch (e) {
      setErrore(messaggio(e, "Non sono riuscito a creare l'avviso."));
    } finally {
      setInCorso(false);
    }
  }

  return (
    <form className="modulo riquadro composizione" onSubmit={invia} noValidate>
      <h3>
        Avviso a {cliente.denominazione}, {conta(scelte.length, "pratica", "pratiche")},{" "}
        {euro(somma(scelte.map((m) => m.da_fatturare)))} di prestazioni
      </h3>
      <p className="aiuto">
        Cassa, IVA e bollo li calcola il programma alla data dell'avviso.
      </p>

      <Interruttore
        titolo="Ritenuta d'acconto su questo avviso"
        spiegazione={
          cliente.sostituto_imposta
            ? "Il cliente è registrato come sostituto d'imposta, quindi di norma sì: il 20% sul compenso."
            : "Il cliente non è registrato come sostituto d'imposta, quindi di norma no."
        }
        acceso={ritenuta}
        cambia={setRitenuta}
      />
      {ritenuta !== cliente.sostituto_imposta && (
        <p className="riga-avviso">
          Stai {ritenuta ? "applicando" : "togliendo"} la ritenuta diversamente da come il
          cliente è registrato. Resta scritto sull'avviso. Se vale sempre, va corretta
          l'anagrafica.
        </p>
      )}

      <div className="imputa-riga">
        <div className="campo campo-largo">
          <label htmlFor="desc-avviso">Descrizione, per chi lo cerca dopo</label>
          <input
            id="desc-avviso"
            value={descrizione}
            placeholder="per esempio: saldo lavori primo semestre"
            onChange={(e) => setDescrizione(e.target.value)}
          />
        </div>
        <div className="campo campo-data">
          <label htmlFor="data-avviso">Data dell'avviso</label>
          <input
            id="data-avviso"
            type="date"
            value={data}
            required
            onChange={(e) => setData(e.target.value)}
          />
        </div>
      </div>

      <Voci
        titolo="Spese anticipate per conto del cliente"
        spiegazione="Diritti, bolli, visure pagati in nome e per conto del cliente (art. 15): fuori dalla base IVA, e contano per la soglia del bollo."
        voci={spese}
        cambia={setSpese}
      />
      <Voci
        titolo="Spese forfettarie"
        spiegazione="Sono compenso a tutti gli effetti anche se si chiamano rimborso: fanno base per cassa, IVA e ritenuta."
        voci={forfettarie}
        cambia={setForfettarie}
      />

      <Interruttore
        titolo="Riaddebita il bollo al cliente"
        spiegazione="Se il bollo è dovuto, compare sull'avviso come passaggio secco: non fa base per cassa, IVA o ritenuta."
        acceso={bollo}
        cambia={setBollo}
      />

      {errore && (
        <p className="errore" role="alert">
          {errore}
          {/prestazione nuova/.test(errore) && (
            <>
              {" "}
              <button
                type="button"
                className="tasto tasto-piccolo"
                onClick={() => window.dispatchEvent(new Event(APRI_PRESTAZIONI_DA_RIVEDERE))}
              >
                Rivedi ora
              </button>
            </>
          )}
        </p>
      )}

      <div className="tasti">
        <button className="tasto tasto-principale" type="submit" disabled={inCorso}>
          {inCorso ? "Sto creando" : "Crea l'avviso in bozza"}
        </button>
        <button type="button" className="tasto tasto-nudo" onClick={lascia}>
          Lascia perdere
        </button>
      </div>
    </form>
  );
}

function Voci({
  titolo,
  spiegazione,
  voci,
  cambia,
}: {
  titolo: string;
  spiegazione: string;
  voci: VoceScritta[];
  cambia: (voci: VoceScritta[]) => void;
}) {
  function aggiorna(indice: number, campo: keyof VoceScritta, valore: string) {
    cambia(voci.map((v, i) => (i === indice ? { ...v, [campo]: valore } : v)));
  }

  return (
    <fieldset className="interruttori">
      <legend>{titolo}</legend>
      <p className="aiuto">{spiegazione}</p>
      {voci.map((v, indice) => (
        <div key={indice} className="imputa-riga">
          <div className="campo campo-largo">
            <label htmlFor={`${titolo}-d-${indice}`}>Descrizione</label>
            <input
              id={`${titolo}-d-${indice}`}
              value={v.descrizione}
              onChange={(e) => aggiorna(indice, "descrizione", e.target.value)}
            />
          </div>
          <div className="campo campo-minuti">
            <label htmlFor={`${titolo}-i-${indice}`}>Euro</label>
            <input
              id={`${titolo}-i-${indice}`}
              inputMode="decimal"
              value={v.importo}
              onChange={(e) => aggiorna(indice, "importo", e.target.value)}
            />
          </div>
          <button
            type="button"
            className="tasto tasto-nudo"
            onClick={() => cambia(voci.filter((_, i) => i !== indice))}
          >
            Togli
          </button>
        </div>
      ))}
      <div>
        <button
          type="button"
          className="tasto tasto-piccolo"
          onClick={() => cambia([...voci, { descrizione: "", importo: "" }])}
        >
          Aggiungi una riga
        </button>
      </div>
    </fieldset>
  );
}

// -------------------------------------------------------- da Fatture Smart

/** La coda di chi fattura: gli incassi che aspettano la loro fattura su
 * Fatture Smart, dal piu' vecchio. Sta in cima alla pagina perche' e' l'unica
 * cosa qui con una scadenza di legge: 12 giorni dall'incasso. Vuota, non
 * occupa spazio. */
/** Una voce della coda "da fare": una fattura nata da un incasso, oppure un acconto o saldo. */
interface VoceDaFare {
  chiave: string;
  tipo: "fattura" | "acconto";
  /** L'identificativo del documento (fattura) o il gruppo di versamenti (acconto). */
  id: string;
  chi: string;
  cosa: string;
  importo: string;
  giorni: number;
  data: string;
}

/** Fatture e acconti insieme, dalla piu' vecchia: la scadenza dei 12 giorni e' la stessa
 * per tutti, e chi lavora non deve ricordarsi di guardare in due posti. */
function vociDaFare(coda: DaPassare[], aMano: VersamentoDaFatturare[]): VoceDaFare[] {
  const voci: VoceDaFare[] = [
    ...coda.map(({ documento: d, giorni_di_attesa }) => ({
      chiave: `f:${d.identificativo}`,
      tipo: "fattura" as const,
      id: d.identificativo,
      chi: d.intestatario || "Senza intestatario",
      cosa: `Fattura, incassata il ${dataBreve(d.data)}`,
      importo: euro(d.totale_documento),
      giorni: giorni_di_attesa,
      data: d.data,
    })),
    ...aMano.map((v) => ({
      chiave: `a:${v.gruppo}`,
      tipo: "acconto" as const,
      id: v.gruppo,
      chi: v.intestatario || "Senza intestatario",
      cosa: `${v.avviso_chiuso ? "Saldo" : "Acconto"}, incassato il ${dataBreve(v.data)}`,
      importo: euro(v.importo),
      giorni: v.giorni_di_attesa,
      data: v.data,
    })),
  ];
  return voci.sort((a, b) => b.giorni - a.giorni || a.chi.localeCompare(b.chi));
}

/** La scheda "Fatture Smart": cosa c'e' da fatturare, dalla piu' urgente, e basta.
 *
 * Tre gruppi per urgenza (scadute, in scadenza, in tempo) invece di un elenco unico: il numero di ognuno dice
 * subito come si sta messi, e quelle in tempo stanno chiuse finche' sono tante. Una riga e' una riga: cliente,
 * cosa, quanto manca, importo e il pulsante; il nome lungo si taglia (si legge intero nella fattura). */
function DaFare({ voci, apri }: { voci: VoceDaFare[]; apri: (v: VoceDaFare) => void }) {
  if (voci.length === 0) {
    return (
      <section className="elenco coda-fatture">
        <p className="riga-fatto">Niente da fatturare: ogni incasso ha la sua fattura.</p>
      </section>
    );
  }
  const gruppi: { chiave: "scaduto" | "avviso" | "in_tempo"; titolo: string; voci: VoceDaFare[] }[] = [
    { chiave: "scaduto", titolo: "Scadute", voci: [] },
    { chiave: "avviso", titolo: "In scadenza", voci: [] },
    { chiave: "in_tempo", titolo: "In tempo", voci: [] },
  ];
  for (const v of voci) gruppi.find((g) => g.chiave === scadenzaFattura(v.giorni, v.data).urgenza)?.voci.push(v);
  const scadute = gruppi[0]!.voci.length;

  const tabella = (elenco: VoceDaFare[]) => (
    <table className="tabella coda-compatta">
      <tbody>
        {elenco.map((v) => {
          const s = scadenzaFattura(v.giorni, v.data);
          return (
            <tr key={v.chiave}>
              <td className="coda-cliente">
                <button type="button" className="tasto-riga" onClick={() => apri(v)} title={v.chi}>
                  <span className="pratica-nome">{v.chi}</span>
                </button>
              </td>
              <td className="coda-cosa narrativa">{v.cosa}</td>
              <td className={`colonna-stato urgenza-${s.urgenza}`}>{s.testo}</td>
              <td className="cifra colonna-importo">{v.importo}</td>
              <td className="colonna-azione">
                <button type="button" className="tasto" onClick={() => apri(v)}>
                  Fai la fattura
                </button>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  return (
    <section className="elenco elenco-primo coda-fatture da-fare">
      <div className="da-fare-testa">
        <p className="da-fare-sintesi">
          <strong>{conta(voci.length, "fattura", "fatture")} da fare</strong>
          {scadute > 0 && <span className="urgenza-scaduto">, {conta(scadute, "scaduta", "scadute")}</span>}
        </p>
        <button type="button" className="tasto tasto-principale" onClick={() => apri(voci[0]!)}>
          Comincia dalla più urgente
        </button>
      </div>
      {gruppi
        .filter((g) => g.voci.length > 0)
        .map((g) =>
          g.chiave === "in_tempo" && g.voci.length > 5 ? (
            <details key={g.chiave} className="gruppo gruppo-coda">
              <summary className="gruppo-testa">
                <h3>{g.titolo}</h3>
                <span className="cifra">{g.voci.length}</span>
              </summary>
              {tabella(g.voci)}
            </details>
          ) : (
            <div key={g.chiave} className="gruppo gruppo-coda">
              <div className="gruppo-testa">
                <h3 className={`urgenza-${g.chiave}`}>{g.titolo}</h3>
                <span className="cifra">{g.voci.length}</span>
              </div>
              {tabella(g.voci)}
            </div>
          ),
        )}
    </section>
  );
}

// ----------------------------------------------------------------- registro

/** Gli avvisi in corso, oppure l'archivio: le due schede che prima erano un
 * registro solo, lungo, in fondo alla pagina. */
function Registro({
  documenti: tutti,
  apri,
  solo,
}: {
  documenti: Documento[];
  apri: (id: string) => void;
  solo: "avvisi" | "archivio";
}) {
  const [cerca, setCerca] = useState("");
  const trovati = tutti.filter((d) => trova(cerca, d.intestatario ?? "", d.numero ? String(d.numero) : "", d.descrizione ?? ""));
  const ordinati = [...trovati].sort((a, b) => (a.data < b.data ? 1 : a.data > b.data ? -1 : 0));
  // La ricerca compare quando l'elenco comincia a essere lungo: su pochi documenti sarebbe rumore.
  const ricerca =
    tutti.length > 8 ? (
      <div className="campo campo-largo">
        <label htmlFor="cerca-documenti">Cerca</label>
        <input
          id="cerca-documenti"
          type="search"
          value={cerca}
          placeholder="cliente, numero o descrizione"
          onChange={(e) => setCerca(e.target.value)}
        />
      </div>
    ) : null;
  const bozze = ordinati.filter((d) => d.tipo === "AVVISO" && d.stato === "BOZZA");
  const daIncassare = ordinati.filter((d) => d.tipo === "AVVISO" && d.stato === "EMESSO");
  // Una fattura in bozza sta nella coda "da passare", non fra le emesse.
  const fiscali = ordinati.filter((d) => d.tipo !== "AVVISO" && d.stato !== "BOZZA");
  const chiusi = ordinati.filter(
    (d) => d.tipo === "AVVISO" && (d.stato === "INCASSATO" || d.stato === "ANNULLATO"),
  );

  if (solo === "avvisi") {
    return (
      <section className="elenco">
        {ricerca}
        <Gruppo
          titolo="In bozza, da controllare e mandare"
          documenti={bozze}
          apri={apri}
          importo={(d) => d.netto_a_pagare}
        />
        <Gruppo
          titolo="Mandati, da incassare"
          documenti={daIncassare}
          apri={apri}
          importo={(d) => d.residuo ?? d.netto_a_pagare}
          totale
        />
      </section>
    );
  }

  return (
    <section className="elenco">
      {ricerca}
      <Gruppo
        titolo="Fatture e note di credito emesse su Fatture Smart"
        documenti={fiscali}
        apri={apri}
        importo={(d) => d.totale_documento}
      />
      {chiusi.length > 0 && (
        <details className="archivio">
          <summary>{conta(chiusi.length, "avviso chiuso", "avvisi chiusi")}, incassati o annullati</summary>
          <Gruppo titolo="" documenti={chiusi} apri={apri} importo={(d) => d.totale_documento} />
        </details>
      )}
    </section>
  );
}

function Gruppo({
  titolo,
  documenti: elenco,
  apri,
  importo,
  totale,
}: {
  titolo: string;
  documenti: Documento[];
  apri: (id: string) => void;
  importo: (d: Documento) => string;
  totale?: boolean;
}) {
  if (elenco.length === 0 && titolo) {
    return (
      <div className="gruppo">
        <div className="gruppo-testa">
          <h3>{titolo}</h3>
          <span className="cifra">nessuno</span>
        </div>
      </div>
    );
  }
  return (
    <div className="gruppo">
      {titolo && (
        <div className="gruppo-testa">
          <h3>{titolo}</h3>
          <span className="cifra">
            {totale ? euro(somma(elenco.map(importo))) : elenco.length}
          </span>
        </div>
      )}
      <table className="tabella">
        <tbody>
          {elenco.map((d) => (
            <tr key={d.identificativo}>
              <td>
                <button type="button" className="tasto-riga" onClick={() => apri(d.identificativo)}>
                  <span className="pratica-nome">
                    {d.intestatario || "Senza intestatario"}
                  </span>
                  <span className="narrativa">
                    {nomeDocumento(d.tipo)}
                    {d.numero && ` n. ${d.numero}`}, {dataCompleta(d.data)}
                    {d.descrizione && `, ${d.descrizione}`}
                  </span>
                </button>
              </td>
              <td className="colonna-stato">
                <span className={`stato stato-doc-${d.stato.toLowerCase()}`}>
                  {nomeStatoDocumento(d.tipo, d.stato)}
                </span>
              </td>
              <td className="cifra colonna-importo">{euro(importo(d))}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------- dettaglio

/* I passi di un lavoro, dall'avviso alla fattura: sono davvero una sequenza,
 * quindi si mostrano come tale. Avviso e fattura sono due documenti, ma per chi
 * fattura e' un lavoro solo, e la sequenza lo tiene insieme. */
const PASSI = ["Avviso in bozza", "Mandato al cliente", "Incassato", "Su Fatture Smart", "Fatturato"];

function passoCorrente(d: Documento): number | null {
  if (d.tipo === "AVVISO") {
    if (d.stato === "BOZZA") return 0;
    if (d.stato === "EMESSO") return 1;
    if (d.stato === "INCASSATO") return 3;
    return null;
  }
  if (d.tipo === "FATTURA") {
    if (d.stato === "BOZZA") return 3;
    if (d.stato === "EMESSO") return 5;
    return null;
  }
  return null;
}

function Passi({ d }: { d: Documento }) {
  const corrente = passoCorrente(d);
  if (corrente === null) return null;
  return (
    <ol className="passi" aria-label="A che punto siamo">
      {PASSI.map((nome, i) => (
        <li
          key={nome}
          className={i < corrente ? "fatto" : i === corrente ? "attuale" : undefined}
          aria-current={i === corrente ? "step" : undefined}
        >
          <span className="passo-segno">{i < corrente ? "✓" : i + 1}</span>
          <span className="passo-nome">{nome}</span>
        </li>
      ))}
    </ol>
  );
}

/** Il dettaglio di un documento, disegnato dalle azioni di chi fattura: in cima
 * chi paga, quanto e a che punto siamo; a destra, sempre in vista, l'unica cosa
 * da fare adesso; a sinistra i conti, per chi li vuole controllare. Prima era una
 * colonna lunga, con le azioni in fondo sotto un paragrafo ciascuna. */
function Dettaglio({
  identificativo,
  tutti,
  apri,
  chiudi,
  dopo,
  posizioneDi,
  numeriUsati,
  registrato,
}: {
  identificativo: string;
  tutti: Documento[];
  apri: (id: string) => void;
  chiudi: () => void;
  dopo: () => Promise<void>;
  posizioneDi: (chiave: string) => { i: number; n: number } | null;
  numeriUsati: string[];
  /** Dopo aver registrato il numero di una fattura: ricarica e passa alla successiva. */
  registrato: (id: string) => Promise<void>;
}) {
  const doc = useCarica<Documento>(() => documenti.dettaglio(identificativo), [identificativo]);

  async function cambiato(nuovo?: Documento) {
    await dopo();
    if (nuovo && nuovo.identificativo !== identificativo) {
      apri(nuovo.identificativo);
    } else {
      await doc.ricarica();
    }
  }

  const d = doc.dati;
  // Una fattura che aspetta Fatture Smart ha la sua pagina a passi, uguale a quella degli acconti.
  if (d !== undefined && d.tipo === "FATTURA" && d.stato === "BOZZA") {
    return (
      <FatturaNormale
        d={d}
        posizione={posizioneDi(`f:${d.identificativo}`)}
        numeriUsati={numeriUsati}
        chiudi={chiudi}
        registrato={() => registrato(d.identificativo)}
      />
    );
  }
  const origine = d?.origine ? tutti.find((x) => x.identificativo === d.origine) : undefined;
  const figli = d ? tutti.filter((x) => x.origine === d.identificativo) : [];

  return (
    <article className="documento">
      <button type="button" className="tasto tasto-nudo torna" onClick={chiudi}>
        ‹ Torna all'elenco
      </button>

      {doc.errore && (
        <p className="errore" role="alert">
          {doc.errore}
        </p>
      )}

      {d === undefined ? (
        doc.errore ? null : <p className="attesa">Un momento.</p>
      ) : (
        <>
          <header className="foglio documento-testa">
            <div className="documento-chi">
              <span className="documento-tipo">
                {nomeDocumento(d.tipo)}
                {d.numero && ` n. ${d.numero}`} del {dataCompleta(d.data)}
                <span className={`stato stato-doc-${d.stato.toLowerCase()}`}>
                  {nomeStatoDocumento(d.tipo, d.stato)}
                </span>
              </span>
              <h2>{d.intestatario || "nessun intestatario registrato"}</h2>
              {d.descrizione && <span className="documento-descrizione">{d.descrizione}</span>}
            </div>
            <div className="documento-quanto">
              {d.tipo === "AVVISO" && d.residuo !== null && !eZero(d.incassato) ? (
                <>
                  <span className="documento-importo">{euro(d.residuo)}</span>
                  <span className="documento-importo-nome">
                    {eZero(d.residuo)
                      ? `incassato per intero (${euro(d.netto_a_pagare)})`
                      : `ancora da incassare, su ${euro(d.netto_a_pagare)}`}
                  </span>
                </>
              ) : (
                <>
                  <span className="documento-importo">{euro(d.netto_a_pagare)}</span>
                  <span className="documento-importo-nome">
                    {d.tipo === "NOTA_CREDITO" ? "a credito del cliente" : "netto da pagare"}
                  </span>
                </>
              )}
            </div>
            <Passi d={d} />
          </header>

          <div className="documento-corpo">
            <div className="documento-conti">
              <Prestazioni d={d} />
              <CalcoloPassoPasso d={d} />
              <IncassiDellAvviso d={d} cambiato={cambiato} />
              {d.tipo === "AVVISO" && (d.stato === "BOZZA" || d.stato === "EMESSO") && (
                <TestoFattura key={d.identificativo} identificativo={d.identificativo} />
              )}
              {d.tipo === "FATTURA" && (
                <details className="archivio">
                  <summary>Quello che è stato copiato in Fatture Smart</summary>
                  <TestoFattura key={d.identificativo} identificativo={d.identificativo} />
                  <SchedaCopia identificativo={d.identificativo} />
                </details>
              )}
              {d.tipo === "AVVISO" && (
                <details className="archivio">
                  <summary>Prospetto di lavoro, in testo</summary>
                  <pre className="macchina">{d.prospetto}</pre>
                </details>
              )}
            </div>

            <aside className="adesso" aria-label="Cosa fare adesso">
              <h3>Adesso</h3>
              {[...d.calcolo.avvisi, ...d.note].map((testo) => (
                <p key={testo} className="riga-avviso">
                  {/* I messaggi del motore nascono minuscoli, per stare dentro
                      un errore; qui stanno da soli e sono frasi. */}
                  {testo.charAt(0).toUpperCase() + testo.slice(1)}
                </p>
              ))}
              <Azioni d={d} cambiato={cambiato} />
              {d.tipo === "AVVISO" && (d.stato === "BOZZA" || d.stato === "EMESSO") && (
                <Stampa d={d} />
              )}
              {d.tipo === "AVVISO" && (d.stato === "BOZZA" || d.stato === "EMESSO") && (
                <Annulla d={d} cambiato={cambiato} />
              )}
              {origine && (
                <p className="adesso-legame">
                  Nasce da{" "}
                  <button type="button" className="tasto tasto-nudo" onClick={() => apri(origine.identificativo)}>
                    {nomeDocumento(origine.tipo).toLowerCase()}
                    {origine.numero && ` n. ${origine.numero}`} del {dataCompleta(origine.data)}
                  </button>
                </p>
              )}
              {figli.map((f) => (
                <p key={f.identificativo} className="adesso-legame">
                  Ha generato{" "}
                  <button type="button" className="tasto tasto-nudo" onClick={() => apri(f.identificativo)}>
                    {nomeDocumento(f.tipo).toLowerCase()}
                    {f.numero && ` n. ${f.numero}`} del {dataCompleta(f.data)}
                  </button>
                </p>
              ))}
              {d.riferimento && <p className="adesso-legame">Rettifica la fattura n. {d.riferimento}.</p>}
            </aside>
          </div>
        </>
      )}
    </article>
  );
}

function Prestazioni({ d }: { d: Documento }) {
  return (
    <table className="tabella parcella">
      <tbody>
        {d.righe.map((r, i) => (
          <tr key={`r-${i}`}>
            <td>{r.descrizione}</td>
            <td className="cifra colonna-importo">{euro(r.importo)}</td>
          </tr>
        ))}
        {d.forfettarie.map((r, i) => (
          <tr key={`f-${i}`}>
            <td>
              {r.descrizione}
              <span className="narrativa">spesa forfettaria</span>
            </td>
            <td className="cifra colonna-importo">{euro(r.importo)}</td>
          </tr>
        ))}
        {d.spese.map((r, i) => (
          <tr key={`s-${i}`}>
            <td>
              {r.descrizione}
              <span className="narrativa">anticipata per conto del cliente, fuori campo IVA</span>
            </td>
            <td className="cifra colonna-importo">{euro(r.importo)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Il calcolo come sulla parcella, una riga per passaggio. Le righe a zero
 * non si mostrano, tranne quelle che per legge ci devono essere: cassa e IVA
 * compaiono sempre, anche quando valgono zero, perche' la loro assenza si
 * leggerebbe come una dimenticanza. */
function CalcoloPassoPasso({ d }: { d: Documento }) {
  const c = d.calcolo;
  const righe: [string, string, string?][] = [];
  righe.push(["Compenso per le prestazioni", c.compenso_prestazioni]);
  if (!eZero(c.spese_forfettarie)) righe.push(["Spese forfettarie", c.spese_forfettarie]);
  righe.push(["Contributo integrativo Cassa", c.cassa]);
  righe.push(["Imponibile IVA", c.base_iva, "sottototale"]);
  righe.push([c.natura_iva ? `IVA non applicata (${c.natura_iva})` : "IVA", c.iva]);
  if (!eZero(c.spese_art_15)) righe.push(["Spese anticipate, fuori campo IVA", c.spese_art_15]);
  if (!eZero(c.bollo_riaddebitato)) righe.push(["Imposta di bollo", c.bollo_riaddebitato]);
  righe.push(["Totale documento", c.totale_documento, "totale"]);
  if (!eZero(c.ritenuta)) righe.push(["Ritenuta d'acconto", `-${c.ritenuta}`]);
  if (!eZero(c.iva_a_carico_pa)) righe.push(["IVA versata dalla PA (scissione)", `-${c.iva_a_carico_pa}`]);
  righe.push(["Netto a pagare", c.netto_a_pagare, "totale"]);

  return (
    <>
      <table className="tabella parcella calcolo">
        <tbody>
          {righe.map(([voce, importo, classe]) => (
            <tr key={voce} className={classe}>
              <td>{voce}</td>
              <td className="cifra colonna-importo">{euro(importo)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {!eZero(c.bollo_dovuto) && eZero(c.bollo_riaddebitato) && (
        <p className="riga-avviso">
          Il bollo da {euro(c.bollo_dovuto)} è dovuto ma non riaddebitato: lo paga
          lo studio.
        </p>
      )}
      <p className={c.quadra ? "riga-fatto" : "errore"}>
        {c.quadra
          ? "Quadratura passata: imponibile, IVA e spese fanno il totale."
          : "La quadratura non torna: imponibile, IVA e spese non fanno il totale. Non mandarlo."}
      </p>
    </>
  );
}

// ------------------------------------------------------------------- azioni

function Azioni({
  d,
  cambiato,
}: {
  d: Documento;
  cambiato: (nuovo?: Documento) => Promise<void>;
}) {
  const { utente } = useSessione();
  if (d.tipo === "AVVISO" && d.stato === "BOZZA") {
    return (
      <div className="azioni">
        <Gesto
          etichetta="Segna come mandato al cliente"
          spiegazione={
            pdfAvvisoAttivo(utente)
              ? "Scarica il PDF, mandalo al cliente, poi segnalo qui."
              : "Mandalo al cliente, poi segnalo qui."
          }
          principale
          esegui={() => documenti.emetti(d.identificativo)}
          cambiato={cambiato}
        />
      </div>
    );
  }
  if (d.tipo === "AVVISO" && d.stato === "EMESSO") {
    return (
      <div className="azioni">
        {/* La chiave riparte il modulo da zero dopo ogni versamento registrato. */}
        <Incassa key={`${d.incassato}-${d.incassi.length}`} d={d} cambiato={cambiato} />
      </div>
    );
  }
  if (d.tipo === "AVVISO" && d.stato === "INCASSATO") {
    return <p className="riga-fatto">Incassato: la fattura è il passo successivo, qui sotto.</p>;
  }
  if (d.tipo === "FATTURA" && d.stato === "EMESSO") {
    return (
      <div className="azioni">
        <p className="riga-fatto">Emessa su Fatture Smart con il numero {d.numero}.</p>
        <Storna d={d} cambiato={cambiato} />
      </div>
    );
  }
  return null;
}

/** Il PDF dell'avviso di parcella da mandare al cliente, verificato riga per
 * riga contro documenti veri dello studio. Solo l'avviso: la fattura e la nota
 * di credito le emette Fatture Smart, e il loro PDF e' il suo. */
function Stampa({ d }: { d: Documento }) {
  const { utente } = useSessione();
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  // Dove Chromium non c'e' (l'immagine online) la rotta risponde 501: meglio
  // non mostrare un pulsante che non puo' funzionare.
  if (!pdfAvvisoAttivo(utente)) return null;

  async function scarica() {
    setErrore(null);
    setInCorso(true);
    try {
      const risposta = await fetch(documenti.stampaUrl(d.identificativo), {
        credentials: "same-origin",
      });
      if (!risposta.ok) {
        const corpo: unknown = await risposta.json().catch(() => null);
        const dettaglio =
          corpo && typeof corpo === "object" && "detail" in corpo
            ? String((corpo as { detail: unknown }).detail)
            : `Il server ha risposto ${risposta.status}.`;
        throw new Error(dettaglio);
      }
      const url = URL.createObjectURL(await risposta.blob());
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      setErrore(e instanceof Error ? e.message : "Non sono riuscito a generare il PDF.");
    } finally {
      setInCorso(false);
    }
  }

  return (
    <div className="gesto">
      <button type="button" className="tasto" disabled={inCorso} onClick={scarica}>
        {inCorso ? "Sto generando" : "Scarica il PDF"}
      </button>

      {errore && (
        <p className="errore" role="alert">
          {errore}
        </p>
      )}
    </div>
  );
}

function Gesto({
  etichetta,
  spiegazione,
  principale,
  esegui,
  cambiato,
}: {
  etichetta: string;
  spiegazione: string;
  principale?: boolean;
  esegui: () => Promise<Documento>;
  cambiato: (nuovo?: Documento) => Promise<void>;
}) {
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  async function vai() {
    setErrore(null);
    setInCorso(true);
    try {
      await cambiato(await esegui());
    } catch (e) {
      setErrore(messaggio(e, "Non è andato a buon fine."));
    } finally {
      setInCorso(false);
    }
  }

  return (
    <div className="gesto">
      <button
        type="button"
        className={principale ? "tasto tasto-principale" : "tasto"}
        disabled={inCorso}
        onClick={vai}
      >
        {etichetta}
      </button>
      <p className="aiuto">{spiegazione}</p>
      {errore && (
        <p className="errore" role="alert">
          {errore}
        </p>
      )}
    </div>
  );
}

/** Annullare chiede una seconda conferma sulla pagina, non una finestra del
 * browser: una finestra si chiude con Invio senza leggerla. E dice cosa
 * succede alle ore, che e' la parte che non si vede. */
function Annulla({
  d,
  cambiato,
}: {
  d: Documento;
  cambiato: (nuovo?: Documento) => Promise<void>;
}) {
  const [chiesto, setChiesto] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  async function vai() {
    setErrore(null);
    setInCorso(true);
    try {
      await cambiato(await documenti.annulla(d.identificativo));
      setChiesto(false);
    } catch (e) {
      setErrore(messaggio(e, "Non sono riuscito ad annullarlo."));
    } finally {
      setInCorso(false);
    }
  }

  if (!chiesto) {
    return (
      <div className="gesto">
        <button type="button" className="tasto" onClick={() => setChiesto(true)}>
          Annulla l'avviso
        </button>
      </div>
    );
  }
  return (
    <div className="gesto conferma">
      <p>
        L'avviso resta nel registro come annullato, e le sue ore tornano da
        fatturare: si possono rimettere in un avviso nuovo.
        {d.stato === "EMESSO" &&
          " È già stato mandato al cliente: avvisalo che quello non vale più."}
      </p>
      <div className="tasti">
        <button type="button" className="tasto tasto-principale" disabled={inCorso} onClick={vai}>
          {inCorso ? "Sto annullando" : "Sì, annullalo"}
        </button>
        <button type="button" className="tasto tasto-nudo" onClick={() => setChiesto(false)}>
          No, tienilo
        </button>
      </div>
      {errore && (
        <p className="errore" role="alert">
          {errore}
        </p>
      )}
    </div>
  );
}

/** Registrare un incasso: intero, parziale, o diviso fra piu' modalita'.
 *
 * Le richieste delle dipendenti del 15 ottobre 2026: pagare un avviso in due modi
 * (50 in contanti e 50 con bonifico) deve essere un incasso solo, e un acconto deve
 * potersi registrare con una spunta che dica "va bene cosi'", scalando il debito.
 * **Una cifra minore del residuo senza la spunta non si registra**: un refuso non deve
 * diventare un acconto. Tutti i conti sono sui centesimi interi (`somma`,
 * `differenza`, `confronta`), mai su numeri a virgola mobile. */
function Incassa({
  d,
  cambiato,
}: {
  d: Documento;
  cambiato: (nuovo?: Documento) => Promise<void>;
}) {
  const residuo = d.residuo ?? d.netto_a_pagare;
  const giaIncassato = !eZero(d.incassato);
  const [righe, setRighe] = useState<{ importo: string; modalita: NomeModalita }[]>([
    // Con un incassato gia' scritto non si propone il residuo intero: chi registra un
    // acconto dopo l'altro lo vedeva gia' scritto e rischiava di confermarlo senza leggere.
    { importo: giaIncassato ? "" : euro(residuo), modalita: "BONIFICO" },
  ]);
  const [data, setData] = useState(oggi());
  const [parziale, setParziale] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  const letti = righe.map((r) => importoDaScritto(r.importo));
  const tuttiLetti = letti.every((x) => x !== null && !eZero(x));
  const totale = tuttiLetti ? somma(letti as string[]) : null;
  const confrontoConResiduo = totale !== null ? confronta(totale, residuo) : null;
  const mancherebbe = totale !== null ? differenza(residuo, totale) : null;
  const eParziale = confrontoConResiduo === -1;
  const troppo = confrontoConResiduo === 1;
  const puoInviare = totale !== null && !troppo && (!eParziale || parziale);

  function cambiaRiga(i: number, valori: Partial<{ importo: string; modalita: NomeModalita }>) {
    setRighe((attuali) => attuali.map((r, j) => (j === i ? { ...r, ...valori } : r)));
  }

  async function invia(evento: FormEvent) {
    evento.preventDefault();
    setErrore(null);
    if (!tuttiLetti) return setErrore("Controlla gli importi: ognuno deve essere una cifra maggiore di zero.");
    setInCorso(true);
    try {
      await cambiato(
        await documenti.incassa(d.identificativo, {
          data_incasso: data,
          versamenti: righe.map((r, i) => ({ importo: letti[i] as string, modalita: r.modalita })),
          parziale: eParziale && parziale,
        }),
      );
    } catch (e) {
      setErrore(messaggio(e, "Non sono riuscito a registrare l'incasso."));
    } finally {
      setInCorso(false);
    }
  }

  return (
    <form className="modulo gesto" onSubmit={invia} noValidate>
      <h3>Registra l'incasso</h3>
      {giaIncassato ? (
        <p className="aiuto">
          Già incassato <strong>{euro(d.incassato)}</strong>: mancano{" "}
          <strong>{euro(residuo)}</strong> su {euro(d.netto_a_pagare)}.
        </p>
      ) : (
        <p className="aiuto">
          La fattura prende la <strong>data del pagamento</strong>, non quella dell'avviso.
        </p>
      )}

      <div className="campo campo-data">
        <label htmlFor="data-incasso">Giorno del pagamento</label>
        <input
          id="data-incasso"
          type="date"
          value={data}
          required
          onChange={(e) => setData(e.target.value)}
        />
      </div>

      <fieldset className="versamenti">
        <legend>Come ha pagato</legend>
        {righe.map((r, i) => (
          <div className="imputa-riga versamento" key={i}>
            <div className="campo campo-minuti">
              <label htmlFor={`incassato-${i}`}>{righe.length > 1 ? `Importo ${i + 1}` : "Euro incassati"}</label>
              <input
                id={`incassato-${i}`}
                inputMode="decimal"
                value={r.importo}
                onChange={(e) => cambiaRiga(i, { importo: e.target.value })}
              />
            </div>
            <div className="campo">
              <label htmlFor={`modalita-${i}`}>Modalità</label>
              <select
                id={`modalita-${i}`}
                value={r.modalita}
                onChange={(e) => cambiaRiga(i, { modalita: e.target.value as NomeModalita })}
              >
                {MODALITA.map((m) => (
                  <option key={m.valore} value={m.valore}>
                    {m.nome}
                  </option>
                ))}
              </select>
            </div>
            {righe.length > 1 && (
              <button
                type="button"
                className="tasto tasto-nudo"
                onClick={() => setRighe((attuali) => attuali.filter((_, j) => j !== i))}
              >
                Togli
              </button>
            )}
          </div>
        ))}
        {righe.length < 5 && (
          <button
            type="button"
            className="tasto tasto-nudo"
            onClick={() =>
              setRighe((attuali) => [
                ...attuali,
                { importo: totale !== null && eParziale && mancherebbe ? euro(mancherebbe) : "", modalita: "CONTANTI" },
              ])
            }
          >
            Ha pagato anche in un altro modo
          </button>
        )}
      </fieldset>

      {giaIncassato && righe.length === 1 && righe[0]!.importo !== euro(residuo) && (
        <button
          type="button"
          className="tasto tasto-nudo"
          onClick={() => cambiaRiga(0, { importo: euro(residuo) })}
        >
          Ha pagato il resto: scrivi {euro(residuo)}
        </button>
      )}

      <p className="aiuto" aria-live="polite">
        {totale === null
          ? "Scrivi le cifre incassate."
          : troppo
            ? `Hai scritto ${euro(totale)}, ma mancano solo ${euro(residuo)}.`
            : eParziale
              ? `Totale ${euro(totale)}: restano ancora ${euro(mancherebbe as string)} da incassare.`
              : `Totale ${euro(totale)}: l'avviso è pagato per intero.`}
      </p>

      {eParziale && (
        <label className="spunta-parziale">
          <input
            type="checkbox"
            checked={parziale}
            onChange={(e) => setParziale(e.target.checked)}
          />
          <span>
            <strong>Pagamento parziale, va bene così.</strong> L'avviso resta aperto per il resto. Il
            debito del cliente scende e il versamento resta scritto.
          </span>
        </label>
      )}
      {(eParziale || giaIncassato) && (
        <p className="aiuto">
          Con un acconto <strong>la fattura non nasce da sola</strong>: l'incasso compare in
          «Fatture Smart» da passare a mano, con la scadenza dei 12 giorni.
        </p>
      )}

      {errore && (
        <p className="errore" role="alert">
          {errore}
        </p>
      )}
      <div>
        <button className="tasto tasto-principale" type="submit" disabled={inCorso || !puoInviare}>
          {inCorso ? "Sto registrando" : eParziale ? "Registra l'acconto" : "Registra l'incasso"}
        </button>
        {eParziale && !parziale && (
          <span className="aiuto"> Per registrare una cifra minore spunta «Pagamento parziale».</span>
        )}
      </div>
    </form>
  );
}

/** I versamenti di un avviso, uno per riga, col debito che scende. Un versamento
 * sbagliato non si cancella: si annulla con un motivo e resta, barrato. */
function IncassiDellAvviso({
  d,
  cambiato,
}: {
  d: Documento;
  cambiato: (nuovo?: Documento) => Promise<void>;
}) {
  const [annullando, setAnnullando] = useState<string | null>(null);
  const [motivo, setMotivo] = useState("");
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  if (d.tipo !== "AVVISO" || d.incassi.length === 0) return null;
  const residuo = d.residuo ?? d.netto_a_pagare;

  async function annulla(v: Incasso) {
    setErrore(null);
    if (!motivo.trim()) return setErrore("Scrivi perché lo annulli: resta scritto accanto al versamento.");
    setInCorso(true);
    try {
      await incassi.annulla(v.identificativo, motivo.trim());
      setAnnullando(null);
      setMotivo("");
      await cambiato();
    } catch (e) {
      setErrore(messaggio(e, "Non sono riuscito ad annullare il versamento."));
    } finally {
      setInCorso(false);
    }
  }

  return (
    <section className="incassi-avviso" aria-label="Incassi di questo avviso">
      <h3>Incassi</h3>
      <p className="narrativa">
        Netto {euro(d.netto_a_pagare)}, incassato {euro(d.incassato)},{" "}
        {eZero(residuo) ? "niente da incassare" : <strong>mancano {euro(residuo)}</strong>}.
      </p>
      <table className="tabella">
        <tbody>
          {d.incassi.map((v) => (
            <tr key={v.identificativo} className={v.annullato ? "versamento-annullato" : undefined}>
              <td>
                <span className="pratica-nome">
                  {dataCompleta(v.data)}, {v.modalita_nome}
                </span>
                <span className="narrativa">
                  registrato da {v.registrato_da_nome || "qualcuno"}
                  {v.annullato && (
                    <>
                      {" "}
                      · annullato da {v.annullato_da_nome}: {v.annullato_motivo}
                    </>
                  )}
                  {!v.annullato && v.da_fatturare_a_mano && !v.fattura_riferimento && (
                    <>
                      {" "}
                      · da passare a mano su Fatture Smart:{" "}
                      <Link to={`/fatturare/fatture-smart?acconto=${encodeURIComponent(v.gruppo)}`}>
                        prepara la fattura
                      </Link>
                    </>
                  )}
                  {!v.annullato && v.fattura_riferimento && <> · fattura n. {v.fattura_riferimento}</>}
                </span>
              </td>
              <td className="cifra colonna-importo">{euro(v.importo)}</td>
              <td className="colonna-stato">
                {!v.annullato &&
                  d.stato === "EMESSO" &&
                  !v.fattura_riferimento &&
                  (annullando === v.identificativo ? null : (
                    <button
                      type="button"
                      className="tasto tasto-nudo"
                      onClick={() => {
                        setAnnullando(v.identificativo);
                        setErrore(null);
                      }}
                    >
                      Annulla
                    </button>
                  ))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {annullando !== null && (
        <div className="gesto">
          <label htmlFor="motivo-annullo">Perché annulli questo versamento?</label>
          <input
            id="motivo-annullo"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="per esempio: cifra sbagliata"
          />
          <div className="tasti">
            <button
              type="button"
              className="tasto tasto-principale"
              disabled={inCorso}
              onClick={() => {
                const v = d.incassi.find((x) => x.identificativo === annullando);
                if (v) void annulla(v);
              }}
            >
              {inCorso ? "Sto annullando" : "Sì, annulla il versamento"}
            </button>
            <button type="button" className="tasto tasto-nudo" onClick={() => setAnnullando(null)}>
              No, tienilo
            </button>
          </div>
        </div>
      )}
      {errore && (
        <p className="errore" role="alert">
          {errore}
        </p>
      )}
    </section>
  );
}

function Storna({
  d,
  cambiato,
}: {
  d: Documento;
  cambiato: (nuovo?: Documento) => Promise<void>;
}) {
  const [aperto, setAperto] = useState(false);
  const [data, setData] = useState(oggi());
  const [motivo, setMotivo] = useState("");
  const [numeroNota, setNumeroNota] = useState("");
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  if (!aperto) {
    return (
      <div className="gesto">
        <button type="button" className="tasto" onClick={() => setAperto(true)}>
          Registra una nota di credito
        </button>
        <p className="aiuto">
          Una fattura non si annulla e non si cancella: si rettifica con una nota
          di credito che la richiama. La nota si emette su Fatture Smart, e qui si
          registra col suo numero, così restano tutte e due nel registro.
        </p>
      </div>
    );
  }

  async function invia(evento: FormEvent) {
    evento.preventDefault();
    setErrore(null);
    if (!motivo.trim()) return setErrore("Serve il motivo: una nota di credito senza non si giustifica.");
    if (!numeroNota.trim()) return setErrore("Serve il numero che Fatture Smart ha dato alla nota.");
    setInCorso(true);
    try {
      await cambiato(await documenti.storna(d.identificativo, data, motivo, numeroNota.trim()));
    } catch (e) {
      setErrore(messaggio(e, "Non sono riuscito a stornarla."));
    } finally {
      setInCorso(false);
    }
  }

  return (
    <form className="modulo gesto" onSubmit={invia} noValidate>
      <h3>Nota di credito sulla fattura n. {d.numero}</h3>
      <div className="imputa-riga">
        <div className="campo campo-largo">
          <label htmlFor="motivo">Motivo</label>
          <input id="motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)} />
        </div>
        <div className="campo campo-data">
          <label htmlFor="data-nota">Data</label>
          <input id="data-nota" type="date" value={data} onChange={(e) => setData(e.target.value)} />
        </div>
        <div className="campo campo-minuti">
          <label htmlFor="numero-nota">Numero su Fatture Smart</label>
          <input id="numero-nota" value={numeroNota} onChange={(e) => setNumeroNota(e.target.value)} />
        </div>
      </div>
      {errore && (
        <p className="errore" role="alert">
          {errore}
        </p>
      )}
      <div className="tasti">
        <button className="tasto tasto-principale" type="submit" disabled={inCorso}>
          {inCorso ? "Sto registrando" : "Registra la nota di credito"}
        </button>
        <button type="button" className="tasto tasto-nudo" onClick={() => setAperto(false)}>
          Lascia perdere
        </button>
      </div>
    </form>
  );
}
